import { useEffect, useState } from 'react';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import { isDateInRange } from '../../products/saudi/utils/dateRangePresets';
import { resolveCrmListViewMode } from './crmConfigDefaults';

/** Cap list rows to configured itemsPerPage. */
export function sliceCrmListItems(items = [], itemsPerPage = 50) {
  const limit = Math.max(1, parseInt(itemsPerPage, 10) || 50);
  return items.slice(0, limit);
}

const DEFAULT_DATE_KEYS = [
  'created_at',
  'updated_at',
  'interaction_date',
  'activity_date',
  'expected_close_date',
  'start_date',
  'end_date',
  'date',
];

/** Resolve the first usable date on a CRM record. */
export function resolveCrmRecordDate(record, dateKeys = DEFAULT_DATE_KEYS) {
  const keys = Array.isArray(dateKeys) ? dateKeys : [dateKeys];
  for (const key of keys) {
    const raw = record?.[key];
    if (raw != null && raw !== '') return raw;
  }
  return null;
}

/** Header date-filter preset range (SaudiHeader) — records without dates stay visible. */
export function crmRecordInDateRange(record, dateRange, dateKeys = DEFAULT_DATE_KEYS) {
  if (!dateRange?.start && !dateRange?.end) return true;
  const keys = Array.isArray(dateKeys) ? dateKeys : [dateKeys];
  for (const key of keys) {
    const raw = record?.[key];
    if (raw != null && raw !== '' && isDateInRange(raw, dateRange)) return true;
  }
  const hasAnyDate = keys.some((key) => {
    const raw = record?.[key];
    return raw != null && raw !== '';
  });
  return !hasAnyDate;
}

/** Alias for ventes-style list helpers. */
export function matchesCrmListDateRange(dateValue, dateRange) {
  return isDateInRange(dateValue, dateRange);
}

/** Filter records by global Saudi header date range. */
export function filterCrmRecordsByDateRange(records, dateRange, dateKeys = DEFAULT_DATE_KEYS) {
  if (!dateRange?.start && !dateRange?.end) {
    return Array.isArray(records) ? records : [];
  }
  return (Array.isArray(records) ? records : []).filter((record) =>
    crmRecordInDateRange(record, dateRange, dateKeys),
  );
}

/** Live list display settings: defaultView, itemsPerPage, header date range. */
export function useCrmListSettings(crmConfig = {}) {
  const { dateRange, setDateRange, preset, onPresetChange } = useSaudiListDateRange();
  const [viewMode, setViewMode] = useState(() => resolveCrmListViewMode(crmConfig.defaultView));

  useEffect(() => {
    setViewMode(resolveCrmListViewMode(crmConfig.defaultView));
  }, [crmConfig.defaultView]);

  return {
    dateRange,
    setDateRange,
    preset,
    onPresetChange,
    viewMode,
    setViewMode,
    itemsPerPage: crmConfig.itemsPerPage ?? 50,
  };
}
